import React from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const AnnouncementBanner: React.FC = () => {
  const [renderedAnnouncement, setRenderedAnnouncement] = React.useState<
    ReturnType<typeof useAuth>["bannerAnnouncement"]
  >(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const navigate = useNavigate();
  const {
    bannerAnnouncement,
    closeBannerAnnouncement,
    dismissBannerAnnouncement,
    trackBannerAnnouncementShown,
    trackBannerAnnouncementClick,
  } = useAuth();

  React.useEffect(() => {
    if (!bannerAnnouncement) {
      return;
    }
    void trackBannerAnnouncementShown();
  }, [bannerAnnouncement?.id]);

  React.useEffect(() => {
    if (bannerAnnouncement) {
      setRenderedAnnouncement(bannerAnnouncement);
      const animationFrame = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
      return () => {
        window.cancelAnimationFrame(animationFrame);
      };
    }

    if (!renderedAnnouncement) {
      return;
    }

    setIsVisible(false);
    const timeout = window.setTimeout(() => {
      setRenderedAnnouncement(null);
    }, 260);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [bannerAnnouncement, renderedAnnouncement]);

  const activeAnnouncement = bannerAnnouncement ?? renderedAnnouncement;

  if (!activeAnnouncement) {
    return null;
  }

  const handleNavigate = (url: string, inApp: boolean) => {
    if (inApp) {
      navigate(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const hasTitle = Boolean(activeAnnouncement.title);
  const hasBody = Boolean(activeAnnouncement.body);

  return (
    <div
      className={`tw-transition-[max-height,opacity,transform,padding] ${
        isVisible
          ? "tw-max-h-56 tw-overflow-visible tw-opacity-100 tw-translate-y-0 tw-pt-2"
          : "tw-max-h-0 tw-overflow-hidden tw-opacity-0 -tw-translate-y-1 tw-pt-0"
      }`}
      style={{
        backgroundColor: "#f5f5f5",
        transitionDuration: isVisible ? "180ms" : "260ms",
      }}
    >
      <div className="custom-container tw-h-auto">
        <div
          className="tw-relative tw-mx-auto tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-3 sm:tw-gap-4 tw-rounded-[10px] tw-py-2 tw-px-3 sm:tw-py-1 sm:tw-px-5"
          style={{
            backgroundColor: "#C7E3E9",
            border: "1px solid #AFD4DD",
            width: "calc(100% - 48px)",
            maxWidth: "1080px",
          }}
        >
          <div className="tw-min-w-0 tw-flex tw-flex-1 tw-items-start sm:tw-items-center tw-gap-3">
            <div
              className="tw-mt-0.5 sm:tw-mt-0 tw-h-8 tw-w-8 sm:tw-h-10 sm:tw-w-10 tw-flex-shrink-0 tw-rounded-full tw-flex tw-items-center tw-justify-center"
              style={{ backgroundColor: "rgba(255,255,255,0.55)", color: "#1E596B" }}
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                className="tw-h-5 tw-w-5 sm:tw-h-7 sm:tw-w-7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: "rotate(-14deg)" }}
              >
                <path
                  d="M5.25 13.05V10.95C5.25 10.3977 5.69772 9.95 6.25 9.95H8.55C8.73934 9.95 8.92475 9.89625 9.08472 9.79503L15.7 5.61075C16.3652 5.19018 17.25 5.66807 17.25 6.4551V17.5449C17.25 18.3319 16.3652 18.8098 15.7 18.3892L9.08472 14.205C8.92475 14.1038 8.73934 14.05 8.55 14.05H6.25C5.69772 14.05 5.25 13.6023 5.25 13.05Z"
                  stroke="currentColor"
                  strokeWidth="1.45"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.2 14.05L9.15 18.2C9.27286 18.737 8.86494 19.25 8.31412 19.25H7.75368C7.38288 19.25 7.05692 19.0047 6.95126 18.6493L5.8 14.05"
                  stroke="currentColor"
                  strokeWidth="1.45"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.05 9.55C19.1691 10.6691 19.1691 12.4809 18.05 13.6"
                  stroke="currentColor"
                  strokeWidth="1.45"
                  strokeLinecap="round"
                />
                <path
                  d="M19.95 7.85C22.0084 9.9084 22.0084 13.2416 19.95 15.3"
                  stroke="currentColor"
                  strokeWidth="1.45"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div className="tw-min-w-0 tw-flex-1 tw-flex tw-flex-col sm:tw-flex-row sm:tw-flex-wrap sm:tw-items-center tw-gap-x-4 tw-gap-y-1.5">
              <div
                className="tw-min-w-0 sm:tw-max-w-[38rem] tw-text-[13px] tw-leading-5 sm:tw-text-sm sm:tw-self-center"
                style={{
                  color: "#1E596B",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                }}
              >
                {hasTitle && (
                  <span style={{ color: "#1E596B" }} className="tw-font-semibold">
                    {activeAnnouncement.title}
                    {hasBody ? ": " : ""}
                  </span>
                )}
                {hasBody && (
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <span>{children}</span>,
                      a: ({ href, children }) => {
                        const isExternal = Boolean(href && /^https?:\/\//.test(href));
                        return (
                          <a
                            href={href}
                            target={isExternal ? "_blank" : undefined}
                            rel={isExternal ? "noopener noreferrer" : undefined}
                            style={{ color: "#1E596B" }}
                            className="tw-font-medium tw-underline tw-underline-offset-2"
                          >
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {activeAnnouncement.body}
                  </ReactMarkdown>
                )}
              </div>

              {activeAnnouncement.cta2_label && activeAnnouncement.cta2_url && (
                <button
                  type="button"
                  onClick={async () => {
                    await trackBannerAnnouncementClick(2);
                    const url = activeAnnouncement.cta2_url as string;
                    handleNavigate(url, url.startsWith("/"));
                  }}
                  className="tw-self-start sm:tw-self-auto tw-text-[13px] sm:tw-text-sm tw-font-medium tw-underline tw-underline-offset-2"
                  style={{ color: "#1E596B" }}
                >
                  {activeAnnouncement.cta2_label} {"→"}
                </button>
              )}

              {activeAnnouncement.cta1_label && activeAnnouncement.cta1_url && (
                <button
                  type="button"
                  onClick={async () => {
                    await trackBannerAnnouncementClick(1);
                    const url = activeAnnouncement.cta1_url as string;
                    handleNavigate(url, url.startsWith("/"));
                  }}
                  className="tw-self-start sm:tw-self-auto tw-text-[13px] sm:tw-text-sm tw-font-medium tw-underline tw-underline-offset-2"
                  style={{ color: "#1E596B" }}
                >
                  {activeAnnouncement.cta1_label} {"→"}
                </button>
              )}

              {activeAnnouncement.allow_dismiss !== false && (
                <button
                  type="button"
                  onClick={async () => {
                    await dismissBannerAnnouncement();
                  }}
                  className="tw-self-start sm:tw-self-auto tw-text-[12px] tw-underline tw-underline-offset-2"
                  style={{ color: "#1E596B" }}
                >
                  Nicht mehr anzeigen
                </button>
              )}
            </div>
          </div>

          <div className="tw-absolute tw-top-2 tw-right-2 sm:tw-static tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-flex-shrink-0 sm:tw-ml-4">
            {activeAnnouncement.allow_close !== false && (
              <button
                type="button"
                aria-label="Banner schließen"
                onClick={async () => {
                  await closeBannerAnnouncement();
                }}
                className="tw-h-8 tw-w-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-leading-none hover:tw-bg-[rgba(255,255,255,0.45)]"
                style={{ color: "#1E596B" }}
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
